import React from 'react';
import { Typography, Grid, Box, IconButton } from '@mui/material';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { COLOR_PRIMARY_DARK, exchangeRates, POINTER_GREEN } from 'src/utils/const';

import {
  checkAdrAnomaly,
  checkListingRecentlyAdded,
  convertCurrency,
  formatCurrency,
  formatDate,
} from 'src/utils/functions';
import { Listing } from 'src/interfaces/idashboard';
import { COLOR_PRIMARY_2, COLOR_TEXT_3 } from 'src/utils/colors';
import { User } from 'src/interfaces/iuser';
import { useSelector } from 'react-redux';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { RecentlyAddedClockIcon } from 'src/App/Dashboards/Market/Components/Chips/RecentlyAddedChip';
import UnavailableIcon from 'src/App/Dashboards/Market/Components/Icons/UnavailableIcon';
import HistoricalPerformanceGraph from './HistoricalPerformanceGraph';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import MainKPITooltip from 'src/App/Dashboards/Market/Components/Tooltips/MainKPITooltip';
import { kpiTooltipConfig } from 'src/App/Dashboards/Market/Pages/OverviewPage/configs/tooltipConfig';

const StatsCard = ({
  title,
  value,
  isDisabled,
  isListingRecentlyAdded,
  upgradeMessage,
  handleOnSampleClick,
  isMobileScreen,
  isAdrAnomaly,
  tooltipKey,
  onClose,
  setMarketUrlBack,
}: {
  title: string;
  value: string;
  isDisabled?: boolean;
  isListingRecentlyAdded: boolean;
  upgradeMessage: any;
  handleOnSampleClick: any;
  isMobileScreen: boolean;
  isAdrAnomaly: boolean;
  tooltipKey?: string;
  onClose: any;
  setMarketUrlBack: () => void;
}) => {
  return (
    <Grid
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignIterms: 'center',
        justifyContent: 'flex-start',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        padding: '40px 20px',
        gap: '10px',
        borderRadius: '20px',
        width: isMobileScreen ? 'calc(100% - 20px)' : 'calc(50% - 10px)',
      }}
    >
      <Typography
        color="textSecondary"
        variant="subtitle2"
        sx={{
          fontSize: '16px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        {title}
        {tooltipKey && (
          <InfoButton
            tooltipContent={
              <MainKPITooltip
                tooltipConfig={kpiTooltipConfig[tooltipKey]}
                matches={isMobileScreen}
              />
            }
          />
        )}
      </Typography>

      <Typography color="textPrimary" variant="h6" sx={{ color: POINTER_GREEN, fontSize: '24px' }}>
        {!isDisabled ? (
          isListingRecentlyAdded ? (
            <AccessTimeIcon />
          ) : isAdrAnomaly && (title.includes('Revenue') || title.includes('Nightly')) ? (
            <UnavailableIcon />
          ) : value === 'locked' ? (
            <LockSharpIcon sx={{ fontSize: 24, color: POINTER_GREEN, cursor: 'pointer' }} />
          ) : (
            value
          )
        ) : (
          <LockSharpIcon
            sx={{ fontSize: 24, color: POINTER_GREEN, cursor: 'pointer' }}
            onClick={() => handleOnSampleClick('expert')}
          />
        )}
      </Typography>
      {isDisabled && upgradeMessage && (
        <Typography
          variant="body1"
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
            maxWidth: '150px',
            textAlign: 'center',
            mt: 1,
          }}
          onClick={() => {
            onClose();
            setMarketUrlBack();
            handleOnSampleClick('expert');
          }}
        >
          {upgradeMessage}
        </Typography>
      )}
    </Grid>
  );
};

const PerformanceData = ({
  data,
  currency,
  handleOnSampleClick,
  isDisabled,
  isEstimator,
  isListingRecentlyAdded,
  isMobileScreen,
  isAdrAnomaly,
  setMarketUrlBack,
  onClose,
}: {
  data: any[] | Listing;
  currency: string;
  isDisabled: boolean;
  handleOnSampleClick?: any;
  isEstimator: boolean;
  isListingRecentlyAdded: boolean;
  isMobileScreen: boolean;
  isAdrAnomaly: boolean;
  setMarketUrlBack: () => void;
  onClose: any;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const currencySymbol = exchangeRates[currency].symbol;
  const upgradeMessage = useUpgradeMesageProFeature();

  let revenuePotential = data['revenuePotential'];

  const formattedRevenuePotential =
    revenuePotential === 'locked'
      ? 'locked'
      : `${currencySymbol}${formatCurrency(String(convertCurrency(currency, revenuePotential)))}`;

  let adr = -1;
  let occupancyRate: number | string = -1;

  adr = data['adrLTM'] ? data['adrLTM'] : -1;
  occupancyRate = data['occupancyRateLTM'] ? data['occupancyRateLTM'] : -1;

  const formattedADR =
    adr !== -1 ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, adr)))}` : '-';
  const formattedOccupancyRate =
    occupancyRate === 'locked' ? 'locked' : occupancyRate !== -1 ? `${occupancyRate}%` : '-';

  const activeDaysCount =
    data['activeDaysCount'] === 'locked'
      ? 'locked'
      : data['activeDaysCount'] && !isNaN(data['activeDaysCount'])
      ? Math.min(365, data['activeDaysCount'])
      : data['activeDaysCount'];

  const revenue = data['annualRevenueLTM'];
  const formattedRevenue =
    data['annualRevenueLTM'] === 'locked'
      ? 'locked'
      : adr !== -1
      ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, revenue)))}`
      : '-';

  const lastPerformanceProcessedMonth = useSelector(state => (state as any).dashboard.lastPerformanceProcessedMonth);
  const endingDate = lastPerformanceProcessedMonth ? lastPerformanceProcessedMonth : data['endingDate'];

  let formattedEndingDate = endingDate;
  try{
    formattedEndingDate = endingDate ? formatDate(endingDate + "-01").split(' ')[1].replace(',', ' ') : '';
  } catch (error) {
    console.error('Error formatting ending date:', error);
  }

  return (
    <>
      <Box
        sx={{
          mt: 4,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '20px',
        }}
      >
        <HistoricalPerformanceGraph
          listingID={data['listingID']}
          handleOnSampleClick={handleOnSampleClick}
          currency={currency}
          setMarketUrlBack={setMarketUrlBack}
          triggerModalClose={onClose}
          showOccupancyGraphOnly={true}
          noTitle={true}
          isAdrAnomaly={isAdrAnomaly}
        />
      </Box>
      <Box
        sx={{
          mt: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          paddingLeft: '20px',
        }}
      >
        <StatsCard
          title="Revenue Potential (LTM)"
          value={formattedRevenuePotential}
          isDisabled={isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'revenuePotentialIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Days Available (LTM)"
          value={activeDaysCount}
          isDisabled={isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'daysAvailableIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Revenue (LTM)"
          value={formattedRevenue}
          isDisabled={isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'revenueIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Occupancy Rate (LTM)"
          value={formattedOccupancyRate}
          isDisabled={isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'occupancyIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Average Nightly Rate (LTM)"
          value={formattedADR}
          isDisabled={false}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'dailyIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        {data['premiumFactor'] !== null && data['premiumFactor'] !== undefined && (
          <StatsCard
            title="Revenue vs. Comps"
            value={
              data['premiumFactor'] === 'locked'
                ? 'locked'
                : `${Number(data['premiumFactor']) > 0 ? '+' : ''}${data['premiumFactor']}%`
            }
            isDisabled={isDisabled}
            isListingRecentlyAdded={false}
            upgradeMessage={upgradeMessage}
            handleOnSampleClick={handleOnSampleClick}
            isMobileScreen={isMobileScreen}
            isAdrAnomaly={false}
            onClose={onClose}
            setMarketUrlBack={setMarketUrlBack}
          />
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{ marginTop: '30px', color: COLOR_TEXT_3, width: '100%', paddingLeft: '20px' }}
      >
        <span style={{ fontWeight: 'bold' }}>LTM:</span> Last Twelve Months (ending {formattedEndingDate})
      </Typography>
    </>
  );
};

export default PerformanceData;
